import gsap from 'gsap';

// Homepage scroll demo: logo 0.6s cubic-bezier(0.63, 0.05, 0, 1), then the
// tagline 0.3s ease-out. Played in full on the first scroll, reversed at the top.
function cubicBezier (x1, y1, x2, y2) {
	const cx = 3 * x1;
	const bx = 3 * (x2 - x1) - cx;
	const ax = 1 - cx - bx;
	const cy = 3 * y1;
	const by = 3 * (y2 - y1) - cy;
	const ay = 1 - cy - by;
	const sampleX = (t) => ((ax * t + bx) * t + cx) * t;
	const sampleY = (t) => ((ay * t + by) * t + cy) * t;
	const sampleDX = (t) => (3 * ax * t + 2 * bx) * t + cx;

	return (x) => {
		if (x <= 0) return 0;
		if (x >= 1) return 1;
		let t = x;
		for (let i = 0; i < 8; i++) {
			const dx = sampleX(t) - x;
			if (Math.abs(dx) < 1e-5) break;
			const slope = sampleDX(t);
			if (Math.abs(slope) < 1e-6) break;
			t = Math.min(1, Math.max(0, t - dx / slope));
		}
		return sampleY(t);
	};
}

function startBackgroundVideo (video) {
	if (! video) return;
	if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

	const start = () => {
		const pending = video.play();
		if (pending && typeof pending.catch === 'function') pending.catch(() => {});
	};

	if (document.readyState === 'complete') start();
	else window.addEventListener('load', start, { once: true });
}

export default function heroHome () {
	const hero = document.querySelector('.hero-home');
	if (! hero) return;

	startBackgroundVideo(hero.querySelector('video.bg-video'));

	const wordmark = document.querySelector('.bloom-wordmark');
	const heroSlot = hero.querySelector('.hero-logo-slot');
	const headerSlot = document.querySelector('.logo-slot');
	const tagline = document.querySelector('.logo-tagline');

	const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
	if (reduce || ! wordmark || ! heroSlot || ! headerSlot) {
		document.body.classList.add('is-settled');
		return;
	}

	const logoEase = cubicBezier(0.63, 0.05, 0, 1);
	const tagEase = cubicBezier(0, 0, 0.58, 1);
	const state = { logo: 0, tag: 0 };
	let openRect = null;

	const menuOpen = () => document.documentElement.classList.contains('is-menu-open');
	const atTop = () => window.scrollY < 1;

	const box = (el) => {
		const rect = el.getBoundingClientRect();
		return { x: rect.left, y: rect.top, w: rect.width, h: rect.height };
	};

	const park = () => {
		wordmark.style.width = '';
		wordmark.style.height = '';
		wordmark.style.transform = '';
		if (tagline) {
			tagline.style.opacity = '';
			tagline.style.transform = '';
		}
	};

	const place = () => {
		if (menuOpen()) {
			park();
			return;
		}

		if (atTop() || ! openRect) openRect = box(heroSlot);
		const from = openRect;
		const to = box(headerSlot);
		const logo = state.logo;
		const x = from.x + (to.x - from.x) * logo;
		const y = from.y + (to.y - from.y) * logo;
		const w = from.w + (to.w - from.w) * logo;
		const h = from.h + (to.h - from.h) * logo;

		wordmark.style.width = w + 'px';
		wordmark.style.height = h + 'px';
		wordmark.style.transform = 'translate(' + x + 'px, ' + y + 'px)';
		wordmark.classList.toggle('is-settled-logo', logo >= 1);

		if (tagline) {
			tagline.style.opacity = '1';
			tagline.style.transform = 'translateY(' + (-100 * (1 - state.tag)) + '%)';
		}

		document.body.classList.toggle('is-settled', logo >= 1 && state.tag >= 1);
	};

	const timeline = gsap.timeline({ paused: true });
	timeline.to(state, {
		logo: 1,
		duration: 0.6,
		ease: logoEase,
		onUpdate: place,
	}, 0);
	timeline.to(state, {
		tag: 1,
		duration: 0.3,
		ease: tagEase,
		onUpdate: place,
	}, 0.6);

	const sync = () => {
		if (menuOpen()) return;
		if (atTop()) {
			timeline.reverse();
			return;
		}
		timeline.play();
	};

	document.body.classList.add('is-logo-ready');
	if (! atTop()) timeline.progress(1);
	place();

	window.addEventListener('scroll', sync, { passive: true });
	window.addEventListener('resize', () => {
		if (atTop()) openRect = box(heroSlot);
		place();
	});
	window.addEventListener('bloom-menu', () => {
		if (menuOpen()) park();
		else place();
	});
}
